import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  ParseReportedFileRequest,
  ParseReportedFileResponse,
  ImportReportedDataRequest,
  ImportReportedDataResponse,
  GetAgreementsForSupplierRequest,
  GetAgreementsForSupplierResponse,
  GetDivisionsForMappingResponse,
} from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

/**
 * Hook to parse reported xlsx file
 * Returns aggregated rows with supplier/division suggestions
 */
export function useParseReportedFile() {
  const { showToast } = useToast();

  return useMutation<
    ParseReportedFileResponse,
    Error,
    ParseReportedFileRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.rebates.historicalReportedImport.parseReportedFile(
        input,
      );
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to parse reported file',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

/**
 * Hook to get all divisions for dropdown
 */
export function useGetDivisionsForMapping() {
  return useQuery<GetDivisionsForMappingResponse>({
    ...orpc.rebates.historicalReportedImport.getDivisionsForMapping.queryOptions(
      { input: {} },
    ),
  });
}

/**
 * Hook to get agreements for a specific supplier
 */
export function useGetAgreementsForSupplier(supplierId: string | null) {
  return useQuery<GetAgreementsForSupplierResponse>({
    ...orpc.rebates.historicalReportedImport.getAgreementsForSupplier.queryOptions(
      { input: { supplierId: supplierId || '' } },
    ),
    enabled: Boolean(supplierId),
  });
}

/**
 * Hook to import reported data with user-confirmed mappings
 */
export function useImportReportedData() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    ImportReportedDataResponse,
    Error,
    ImportReportedDataRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.rebates.historicalReportedImport.importReportedData(
        input,
      );
    },
    onSuccess: (data) => {
      if (data.success) {
        showToast({
          title: 'Import Complete',
          message: `Created ${data.breakdownsCreated} breakdown records. Total: $${data.totalAmount.toLocaleString()}`,
          color: 'success',
          icon: 'check',
        });

        // Invalidate relevant queries
        void queryClient.invalidateQueries({
          queryKey: ['rebate-agreements'],
        });
        void queryClient.invalidateQueries({
          queryKey: ['calculated-breakdowns'],
        });
      }
    },
    onError: (error) => {
      showToast({
        title: 'Import Failed',
        message: error.message || 'Failed to import reported data',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
